<template>
    <focus-trap
        class="vf-dropdown"
        :class="dropdownClass"
        :active="focusTrapActive"
        :paused="isFocusTrapPaused"
        :return-focus-on-deactivate="returnFocusOnDeactivate"
        :focus-on-activate="false"
        deactivate-on-outside-click
        deactivate-on-esc
        @deactivated="hide"
        v-inner-focus="toggleOnEnter"
        v-hovered="toggleOnEnter"
    >
        <slot
            name="trigger"
            :aria-expanded="isDropdownOpen.toString()"
            :aria-owns="uid"
            :aria-haspopup="ariaHaspopup"
        >
            <vf-button
                class="vf-dropdown__trigger js-trigger"
                :aria-expanded="isDropdownOpen.toString()"
                :aria-owns="uid"
                :aria-haspopup="ariaHaspopup"
                :disabled="disabled"
                is-cta
                :palette="palette"
                @click.native.passive="$emit('trigger-clicked')"
            >
                <slot name="trigger-contents" />
            </vf-button>
        </slot>
        <transition
            name="fade-instant"
            @enter="determineSmartPosition"
            @after-exit="clearSmartPosition"
        >
            <div
                v-if="isDropdownOpen"
                class="vf-dropdown-wrapper vf-tooltip"
                :id="uid"
                :aria-hidden="!isDropdownOpen"
                :aria-expanded="isDropdownOpen"
                role="tooltip"
            >
                <div
                    class="vf-dropdown__tooltip-content vf-tooltip-content vf-secondary-text"
                    :class="{ 'vf-scroll': hasScrollBar }"
                >
                    <slot name="content" />
                </div>
            </div>
        </transition>
    </focus-trap>
</template>

<script>
import { FocusTrap } from '@vf/vue-focus-trap';
import { $ } from 'commons/util/dom.js';
import vfButton from '../buttons/vf-button.vue';
import { hovered } from '../directives/hovered.js';
import { innerFocus } from '../directives/inner_focus.js';
import { PALETTES } from 'design-assets/constants/palettes.js';
import VueTypes from 'commons/util/vue-types.js';
import { uniqueId } from 'commons/util/util.js';

const DROPDOWN_WRAPPER = '.vf-dropdown-wrapper';
const dropdownId = uniqueId('dropdown');

/**
 * Generic Dropdown
 * A dropdown component that takes a prop to tell it when it is open or not.
 * By default, shows the accent cta button as the trigger.
 * Helpful when the state of the dropdown needs to be modified externally.
 */
export default {
    props: {
        /**
         * Controls if the dropdown is open or not. Open if true, closed otherwise
         */
        isDropdownOpen: VueTypes.bool.isRequired,
        /**
         * List of positions that the dropdown will try to display itself in. If it extends outside of "container",
         * then the dropdown will try to display itself in the next position.
         * Will default to the first position if all of them fail
         */
        positions: VueTypes.array.def([ 'bottom' ]),
        /**
         * Sets the disabled attribute on the default trigger
         */
        disabled: VueTypes.bool.def(false),
        /**
         * Provide support to open the dropdown on hover is this value is true
         */
        activateOnHover: VueTypes.bool.def(false),
        /**
         * Displays a scroll bar capping the dropdown at 250px height if true
         */
        hasScrollBar: VueTypes.bool.def(false),
        /**
         * If given, container that the dropdown must stay contained within. Used in conjunction with positions
         */
        container: HTMLElement,
        /**
         * Controls whether or not the dropdown should trap focus. If true, focus cannot escape the dropdown
         */
        shouldTrapFocus: VueTypes.bool,
        /**
         * Controls whether or not the dropdown will return focus to the trigger when closing
         */
        returnFocusOnDeactivate: VueTypes.bool.def(false),

        isFocusTrapPaused: VueTypes.bool.def(false),

        ariaHaspopup: VueTypes.oneOf([
            'menu',
            'listbox',
            'tree',
            'grid',
            'dialog',
        ]).def('menu'),
    },

    created() {
        this.uid = dropdownId();
        this.palette = PALETTES.ACCENT;
    },

    data() {
        return {
            computedPosition: null,
        };
    },

    mounted() {
        if (!this.hasSmartPositioning) {
            this.computedPosition = this.positions[0];
        }
        else if (this.isDropdownOpen) {
            this.determineSmartPosition();
        }
    },

    computed: {
        hasSmartPositioning() {
            return this.positions.length > 1;
        },
        dropdownClass() {
            return {
                [`vf-${this.computedPosition}`]: this.computedPosition,
                'vf-dropdown--is-open': this.isDropdownOpen,
            };
        },
        focusTrapActive() {
            return (
                this.shouldTrapFocus
                && !this.activateOnHover
                && this.isDropdownOpen
            );
        },
        showOverlay() {
            return !this.activateOnHover && this.isDropdownOpen;
        },
    },

    methods: {
        hide() {
            this.$emit('hide');
        },

        determineSmartPosition() {
            if (this.hasSmartPositioning) {
                this.attemptPosition(this.positions);
            }
        },

        clearSmartPosition() {
            this.computedPosition = null;
        },

        attemptPosition(positions = []) {
            if (positions.length === 0) {
                this.fallbackPosition();
                return;
            }
            const position = positions[0];
            // 1 apply class
            this.computedPosition = position;
            // 2 next tick
            this.$nextTick().then(() => {
                // 3 test if the current dropdown extends outside the container
                const elBox = $(
                    DROPDOWN_WRAPPER,
                    this.$el,
                ).getBoundingClientRect();
                const windowBox = (
                    this.container || window.document.body
                ).getBoundingClientRect();

                // If it does, then recursively try to see if the next position is within the container
                if (this.protrudesOutsideWindow(elBox, windowBox)) {
                    const next = positions.slice(1);
                    this.attemptPosition(next);
                }
                else {
                    this.smartVisible = true;
                }
            });
        },

        fallbackPosition() {
            this.computedPosition = this.positions[0];
            this.smartVisible = true;
        },

        protrudesOutsideWindow(elBox, windowBox) {
            return (
                elBox.left < windowBox.left
                || elBox.right > windowBox.right
                || elBox.top < windowBox.top
                || elBox.bottom > windowBox.bottom
            );
        },
        toggleOnEnter(isEntered) {
            if (this.activateOnHover && !this.disabled) {
                this.$emit('dropdown-entered', isEntered);
            }
        },
    },

    directives: {
        hovered,
        innerFocus,
    },

    components: {
        vfButton,
        FocusTrap,
    },
};
</script>

<style lang="scss">
@import '../../../css/variables/module';
@import './dropdown';
</style>
